import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Avatar, Drawer } from 'antd';

import { UserContext } from '@/app/UserProvider';
import AppleIcon from '@/assets/v2/apple.svg?react';
import ArrowRight from '@/assets/v2/download-all.svg?react';
import DuetEN from '@/assets/v2/duet-en.png';
import Duet from '@/assets/v2/duet-zh.png';
import EditorEN from '@/assets/v2/edit-en.png';
import Editor from '@/assets/v2/edit-zh.png';
import { useResponsive } from '@/hooks/useResponsive';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { generateInternalSSOUrl, isInternalDomain } from '@/utils/const';
import { openDownloadInNewWindow, safeDownload } from '@/utils/downloadHelper';
import { getRecommendedDownloadType } from '@/utils/systemDetector';
import { GlassGridBackground } from '@/v2/components/GlassGridBackground';

// Windows 图标
const WindowsIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 12V6.75L9.5 5.7V11.8L3 12ZM3 17.25V12L9.5 12.2V18.3L3 17.25ZM10 5.55L21 3.6V11.7L10 11.8V5.55ZM10 12.2L21 12.1V20.3L10 18.5V12.2Z" />
  </svg>
);

/**
 * 第一屏组件
 */
const isChinese = (language: string) => {
  return ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'].some((lang) =>
    language.startsWith(lang),
  );
};
export const FirstScreen = ({ isMobile, isFlat }: { isMobile: boolean; isFlat: boolean }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'editor' | 'duet'>('editor');
  const [recommendedType, setRecommendedType] = useState<
    'apple' | 'intel' | 'windows' | 'linux' | null
  >(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { isMobile: actualMobile } = useResponsive();
  const { user } = useContext(UserContext);
  const { downloadInfo } = useDownloadInfo();
  const navigate = useNavigate();
  // 初始化时检测用户系统
  useEffect(() => {
    const type = getRecommendedDownloadType();
    setRecommendedType(type);
  }, []);

  // 根据系统类型获取对应的图标和文案
  const downloadConfig = useMemo(() => {
    switch (recommendedType) {
      case 'apple':
        return {
          icon: <AppleIcon className="translate-y-[-1.2px]" />,
          text: t('home.screen1.downloadApple'),
        };
      case 'intel':
        return {
          icon: <AppleIcon />,
          text: t('home.screen1.downloadIntel'),
        };
      case 'windows':
        return {
          icon: <WindowsIcon />,
          text: t('home.screen1.downloadWindows'),
        };
      default:
        // 默认显示 Mac 版本
        return {
          icon: <AppleIcon />,
          text: t('home.screen1.downloadMac'),
        };
    }
  }, [recommendedType, t]);

  // Tab 切换按钮
  const TabButtons = (
    <div className="bg-background-primary flex w-full flex-col items-start rounded-[8px]">
      <div className="flex flex-col  gap-[15px] p-4  ">
        <div className="bg-border-split flex w-fit gap-px rounded-[4px] p-[2px] text-[16px] font-medium  leading-[16px]">
          <div
            onClick={() => setActiveTab('editor')}
            className={`font-manrope cursor-pointer rounded-[3px] px-3 py-1 ${
              activeTab === 'editor'
                ? 'bg-background-secondary text-content-primary'
                : 'text-content-contrast can-hover:hover:text-[#8B8B8B]'
            }`}
          >
            Editor
          </div>
          <div
            onClick={() => setActiveTab('duet')}
            className={`font-manrope cursor-pointer  rounded-[3px] px-3 py-1 ${
              activeTab === 'duet'
                ? 'bg-background-secondary text-content-primary'
                : 'text-content-contrast can-hover:hover:text-[#8B8B8B]'
            }`}
          >
            Duet Space
          </div>
        </div>
        <div className="bg-border-split h-[0.5px] w-full" />
        {/* 两个描述重叠，容器高度取最大值 */}
        <div className="relative w-full">
          {/* Duet 描述 - 始终占位撑高度（因为它更长），用 opacity 控制显示 */}
          <div
            className={`text-[14px] leading-normal  transition-opacity duration-300 ${activeTab === 'duet' ? 'opacity-80' : 'opacity-0'}`}
          >
            {t('home.screen1.duetDescription')}
          </div>
          {/* Editor 描述 - absolute 定位覆盖 */}
          <div
            className={` absolute inset-0 text-[14px] leading-normal  transition-opacity duration-300 ${activeTab === 'editor' ? 'opacity-80' : 'opacity-0'}`}
          >
            {t('home.screen1.editorDescription')}
          </div>
        </div>
      </div>
    </div>
  );

  // 处理登录
  const handleLogin = () => {
    if (isInternalDomain()) {
      window.location.href = generateInternalSSOUrl();
    } else {
      sessionStorage.setItem('pendingRedirect', '/dashboard');
      window.location.href = '/login?redirect=/dashboard';
    }
  };

  // 复制链接
  const handleShareLink = () => {
    const downloadUrl = `${window.location.origin}/download`;
    navigator.clipboard
      .writeText(downloadUrl)
      .then(() => {
        toast.success(t('home.screen1.mobileDrawer.linkCopied'));
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };
  const handleDownload = useCallback(async () => {
    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      navigate('/download');
      return;
    }

    let targetDownloadInfo = null;

    // 根据推荐类型获取下载信息
    switch (recommendedType) {
      case 'apple':
        targetDownloadInfo = downloadInfo.arm;
        break;
      case 'intel':
        targetDownloadInfo = downloadInfo.intel;
        break;
      case 'windows':
        targetDownloadInfo = downloadInfo.windows;
        break;
      default:
        // 不支持的系统，跳转到下载页面
        navigate('/download');
        return;
    }

    if (targetDownloadInfo) {
      try {
        // 尝试安全下载
        const downloadSuccess = await safeDownload(targetDownloadInfo);

        if (!downloadSuccess) {
          // 如果下载失败，尝试在新窗口打开
          const downloadUrl = targetDownloadInfo.dmgUrl || targetDownloadInfo.url;
          if (downloadUrl) {
            openDownloadInNewWindow(downloadUrl);
          } else {
            // 最后的备用方案：跳转到下载页面
            navigate('/download');
          }
        }
      } catch (error) {
        console.error('NavBar 下载失败:', error);
        // 出错时跳转到下载页面
        navigate('/download');
      }
    } else {
      // 没有下载信息，跳转到下载页面
      navigate('/download');
    }
  }, [downloadInfo, recommendedType, navigate]);

  // 下载按钮
  const DownloadButtons = (
    <div
      className={`font-4 flex shrink-0 items-center justify-between gap-4 ${isFlat ? 'w-[210px] flex-col justify-end' : 'flex-row'}`}
    >
      <div
        className={`can-hover:hover:opacity-80 flex min-h-[38px]  ${isFlat && 'w-full'} bg-content-primary text-background-primary cursor-pointer items-center justify-center gap-1.5 rounded-[6px] px-3 py-2 font-medium transition-opacity`}
        onClick={() => (actualMobile ? setMobileDrawerOpen(true) : handleDownload())}
      >
        {downloadConfig.icon}
        {actualMobile ? t('home.screen1.quickStart') : downloadConfig.text}
      </div>
      <div
        onClick={() => navigate('/download')}
        className={`bg-background-primary/10 group flex min-h-[38px] flex-1 cursor-pointer items-center justify-center border px-3 py-2 backdrop-blur-[2.5px] ${isFlat ? 'w-full' : 'border font-medium transition-all'} border-content-title text-content-primary rounded-[6px]`}
      >
        {t('home.screen1.downloadAll')}
        <ArrowRight className="ml-0 w-0 opacity-0 transition-all duration-300 group-hover:ml-2 group-hover:w-4 group-hover:opacity-100" />
      </div>
    </div>
  );

  // 图片区域
  const ImageArea = (
    <div className="relative size-full">
      {/* Editor 图片占位 */}
      <div
        className={`size-full rounded-[6px] transition-opacity  duration-500 ${
          activeTab === 'editor' ? 'opacity-100' : 'absolute inset-0 opacity-0'
        }`}
      >
        <img src={isChinese(i18n.language) ? Editor : EditorEN} />
      </div>

      {/* Duet 图片占位 */}
      <div
        className={`w-full  rounded-[6px]  transition-opacity duration-500 ${
          activeTab === 'duet' ? 'opacity-100' : 'absolute inset-0 opacity-0'
        }`}
      >
        <img src={isChinese(i18n.language) ? Duet : DuetEN} />
      </div>
    </div>
  );
  if (isMobile) {
    return (
      <>
        <div className="relative mx-auto flex min-h-[600px] w-full items-center justify-center overflow-hidden px-4 lg:px-0">
          {/* 毛玻璃格子背景 */}
          <GlassGridBackground />
          <div
            className={`relative z-10 flex items-center ${isFlat ? 'flex-col gap-6' : 'flex-row gap-16'}`}
          >
            {/* 左侧内容区 */}
            <div
              className={`${isFlat ? 'w-full' : 'w-[396px]'} flex flex-col items-center justify-center gap-[44px] px-4 text-left lg:px-0`}
            >
              {/* 标题 */}
              <div className="text-content-primary w-auto  gap-[44px] text-[40px] font-semibold leading-[1.2]  md:text-[48px]">
                <div>
                  <Trans
                    i18nKey="home.screen1.title1"
                    components={{
                      highlight: <span className="bg-[#29E2CCff] bg-clip-text text-transparent" />,
                    }}
                  />
                </div>
                <div>
                  <Trans
                    i18nKey="home.screen1.title2"
                    components={{
                      highlight: <span className="bg-[#29E2CCff] bg-clip-text text-transparent" />,
                    }}
                  />
                </div>
              </div>

              {/* Tab 切换 */}
              <div
                className={`flex  w-[210px] items-center  justify-center text-sm leading-normal md:text-[16px] ${isFlat ? 'flex-row' : 'flex-col'} gap-[80px]`}
              >
                {/* 下载按钮 */}
                {DownloadButtons}
              </div>
            </div>
          </div>
        </div>

        {/* 移动端半屏抽屉 */}
        <Drawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          placement="bottom"
          height="auto"
          closable={false}
          styles={{
            body: { padding: 0, background: '#fff', borderRadius: '16px 16px 0 0' },
            wrapper: { borderRadius: '16px 16px 0 0' },
          }}
          rootClassName="mobile-quick-start-drawer"
        >
          <div className="flex flex-col rounded-t-2xl bg-white text-black">
            {/* 标题栏 */}
            <div className="flex items-center justify-between px-6 py-5">
              <span className="text-lg font-semibold">{t('home.screen1.mobileDrawer.title')}</span>
              <div
                className="flex size-6 cursor-pointer items-center justify-center"
                onClick={() => setMobileDrawerOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>

            {/* 说明文字 */}
            <div className="px-6 pb-6 text-sm leading-normal text-gray-600">
              {t('home.screen1.mobileDrawer.description')}
            </div>

            {/* 登录按钮或用户信息 */}
            <div className="px-6 pb-4">
              {user ? (
                <div className="flex items-center gap-3 rounded-[6px] bg-gray-50 p-4">
                  <Avatar
                    src={user.avatar}
                    size={40}
                    shape="square"
                    style={{
                      borderRadius: '4px',
                      border: '1px solid #71C9C0',
                      background: '#71C9C038',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#32EDDA',
                    }}
                  >
                    {user.displayName?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="font-medium text-black">
                      {user.displayName || user.username || 'User'}
                    </span>
                    <span className="text-sm text-gray-500">{user.email || user.mail || ''}</span>
                  </div>
                </div>
              ) : (
                <div
                  className="flex h-14 w-full cursor-pointer items-center justify-center rounded-[6px] bg-black text-base font-medium text-white"
                  onClick={handleLogin}
                >
                  {t('navbar.login')}
                </div>
              )}
            </div>

            {/* 分隔线 */}
            <div className="flex items-center px-6 py-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="px-4 text-sm text-gray-400">
                {t('home.screen1.mobileDrawer.or')}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* 分享下载地址按钮 */}
            <div className="px-6 pb-6">
              <div
                className="flex h-14 w-full cursor-pointer items-center justify-center rounded-[6px] border border-gray-300 text-base font-medium text-black"
                onClick={handleShareLink}
              >
                {t('home.screen1.mobileDrawer.shareLink')}
              </div>
            </div>

            {/* 底部社交链接 */}
            <div className="flex items-center justify-center gap-8 border-t border-gray-100 py-6">
              <a
                href="https://x.com/codeflicker"
                target="_blank"
                rel="noopener noreferrer"
                className="can-hover:hover:text-black flex items-center gap-2 text-sm text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="font-manrope">X/Twitter</span>
              </a>
              <a
                href="https://discord.gg/codeflicker"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-black"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="currentColor"
                >
                  <path d="M16.942 1.556a16.3 16.3 0 0 0-4.126-1.3c-.175.32-.386.757-.527 1.098a15.175 15.175 0 0 0-4.573 0c-.14-.34-.366-.778-.543-1.098a16.45 16.45 0 0 0-4.129 1.3A17.392 17.392 0 0 0 .182 13.218a16.632 16.632 0 0 0 5.062 2.605c.41-.561.772-1.155 1.084-1.785a10.63 10.63 0 0 1-1.706-.831c.143-.106.283-.217.418-.33a11.664 11.664 0 0 0 10.118 0c.137.113.277.224.418.33-.544.328-1.116.606-1.71.832a12.52 12.52 0 0 0 1.084 1.785 16.46 16.46 0 0 0 5.064-2.595c.415-4.396-.709-8.209-2.992-11.662zM6.678 10.842c-.988 0-1.798-.922-1.798-2.045s.793-2.047 1.798-2.047 1.815.922 1.798 2.047c.001 1.123-.793 2.045-1.798 2.045zm6.644 0c-.988 0-1.798-.922-1.798-2.045s.793-2.047 1.798-2.047 1.815.922 1.798 2.047c0 1.123-.793 2.045-1.798 2.045z" />
                </svg>
                <span className="font-manrope">Discord</span>
              </a>
            </div>
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <div className="relative mx-auto  flex min-h-[688px] w-full flex-col items-center justify-center overflow-hidden px-[28px] pb-[48px] pt-[152px] md:px-[120px] lg:px-0">
      {/* 毛玻璃格子背景 */}
      <GlassGridBackground />
      <div
        className={`relative z-10 flex w-full max-w-screen-lg flex-1 items-center ${isFlat ? 'w-full flex-col gap-6' : 'flex-row gap-[120px]'}`}
      >
        {/* 左侧内容区 */}
        <div
          className={`${isFlat ? 'mt-5 w-full' : 'w-[371px]'} flex flex-col items-start px-4 text-left lg:px-0`}
        >
          {/* 标题 */}
          <div className="text-content-primary mb-[28px] text-[40px] font-semibold leading-[1.2]  md:text-[48px]">
            <Trans
              i18nKey="home.screen1.title1"
              components={{
                highlight: <span className="bg-[#29E2CCff] bg-clip-text text-transparent" />,
              }}
            />
            <br />
            <Trans
              i18nKey="home.screen1.title2"
              components={{
                highlight: <span className="bg-[#29E2CCff] bg-clip-text text-transparent" />,
              }}
            />
          </div>

          {/* Tab 切换 */}
          <div
            className={`flex  w-full text-sm leading-normal md:text-[16px] ${isFlat ? 'flex-row items-center' : 'flex-col'} gap-[80px]`}
          >
            {TabButtons}

            {/* 下载按钮 */}
            {DownloadButtons}
          </div>
        </div>

        {/* 右侧图片区域 */}

        <div className="border-background-primary min-w-[400px] flex-1 self-stretch rounded-[10px] border-4 border-opacity-50">
          {ImageArea}
        </div>
      </div>
    </div>
  );
};

export default FirstScreen;